/*
 * overlay.js — Geteilte Overlay-/Dialog-Basis für ALLE Overlays der Chrome
 * (Cart-Drawer, Menu-Drawer, Search-Overlay, Consent-Settings, Compat-Finder-Popup …).
 * docs/06 §Geteilte Chrome: EIN Dialog-/Overlay-Pattern.
 *
 * Liefert: aria-modal-Semantik, Fokus-Trap, Escape (schließt oberstes), Body-Scroll-Lock
 * (gezählt für verschachtelte Overlays, Scrollbar-Kompensation gegen Layout-Shift),
 * Fokus-Restore auf den Trigger, prefers-reduced-motion, safe-area (CSS).
 *
 * Öffentliche API (window.Homedia.overlay):
 *   open(panel, { trigger, onClose, labelledby })  → öffnet ein Panel-Element
 *   close(panel)                                    → schließt es (Exit-Animation via data-state)
 *   toggle(panel, opts)
 *   isOpen(panel) / current()                       → Stack-Helfer
 *   getFocusable(container)                          → Liste fokussierbarer Elemente
 *
 * Deklaratives Wiring (kein Komponenten-JS nötig):
 *   <button data-overlay-open="cart-drawer">…   öffnet #cart-drawer
 *   <… data-overlay-close>…                       schließt das umschließende/oberste Overlay
 * Panels starten mit [hidden] und tragen idealerweise role="dialog".
 */
(function () {
  'use strict';
  var Homedia = (window.Homedia = window.Homedia || {});
  if (Homedia.overlay) return;

  var FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
    '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not([tabindex^="-"])'
  ].join(',');

  var stack = [];        // [{ panel, trigger, onClose }]
  var lockCount = 0;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getFocusable(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      }
    );
  }

  // ---- Fokus-Restore nach Live-Re-Render (F018) ----
  // Re-Renders (Bundled Section Rendering, JS-Neuaufbau) ersetzen das Panel-Markup komplett —
  // ein fokussierter Button wird zerstört, der Fokus fällt auf <body> und Tab liefe hinter das
  // modale Dialog. Fokus ohne Scroll-Sprung zurück ins Panel setzen: bevorzugt das Nachfolge-
  // Element (preferredSelector), sonst sichtbarer [data-overlay-close], sonst das Panel selbst.
  // Kandidaten in [hidden]-Subtrees überspringen (focus() wäre dort ein stilles No-op) —
  // z. B. existiert im Cart-Drawer jede Zeile doppelt (Just-Added-Block + Voll-Liste).
  function firstVisibleIn(panel, selector) {
    var nodes = panel.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].closest('[hidden]')) return nodes[i];
    }
    return null;
  }

  function restoreFocus(panel, preferredSelector) {
    if (!panel) return;
    var active = document.activeElement;
    if (active && active !== document.body && panel.contains(active)) return; // Fokus intakt
    var target = (preferredSelector && firstVisibleIn(panel, preferredSelector)) ||
      firstVisibleIn(panel, '[data-overlay-close]') || panel;
    if (target && target.focus) {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
  }

  function lockScroll() {
    if (lockCount === 0) {
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--hm-sbw', (sbw > 0 ? sbw : 0) + 'px');
      document.body.classList.add('hm-scroll-locked');
    }
    lockCount++;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('hm-scroll-locked');
      document.documentElement.style.removeProperty('--hm-sbw');
    }
  }

  function closeDuration(panel) {
    if (reducedMotion()) return 0;
    var attr = parseInt(panel.getAttribute('data-close-ms'), 10);
    return isNaN(attr) ? 220 : attr;
  }

  // Zugehöriger Scrim (Klick-außerhalb-schließt) = direktes Geschwister-Element mit .hm-scrim
  // davor (Cart-/Wishlist-Drawer). Dialoge mit eigenem internen Scrim (z. B. Compat-Finder) haben
  // KEIN solches Geschwister → null, sie regeln ihren Scrim selbst über data-state.
  function getScrim(panel) {
    var s = panel.previousElementSibling;
    return s && s.classList && s.classList.contains('hm-scrim') ? s : null;
  }

  function open(panel, opts) {
    if (!panel || isOpen(panel)) return;
    // Pendentes Close-Cleanup abbrechen (Re-Open während der Exit-Animation) — sonst
    // versteckt der alte Timer/transitionend-Listener das frisch geöffnete Panel.
    if (panel.__hmCancelClose) { panel.__hmCancelClose(); panel.__hmCancelClose = null; }
    opts = opts || {};
    var trigger = opts.trigger || (document.activeElement !== document.body ? document.activeElement : null);

    panel.hidden = false;
    if (!panel.getAttribute('role')) panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    if (opts.labelledby) panel.setAttribute('aria-labelledby', opts.labelledby);
    if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');

    lockScroll();
    stack.push({ panel: panel, trigger: trigger, onClose: opts.onClose });

    // Scrim einblenden (Backdrop + Klick-außerhalb-schließt via data-overlay-close).
    var scrim = getScrim(panel);
    if (scrim) scrim.hidden = false;

    // Enter-Animation: erst sichtbar, dann im nächsten Frame data-state=open setzen.
    requestAnimationFrame(function () {
      panel.setAttribute('data-state', 'open');
      if (scrim) scrim.setAttribute('data-open', '');
      var target = panel.querySelector('[autofocus]') || getFocusable(panel)[0] || panel;
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    });

    panel.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
  }

  function close(panel) {
    var idx = -1;
    for (var i = stack.length - 1; i >= 0; i--) { if (stack[i].panel === panel) { idx = i; break; } }
    if (idx === -1) return;
    var entry = stack.splice(idx, 1)[0];

    panel.setAttribute('data-state', 'closed');
    panel.removeAttribute('aria-modal');
    unlockScroll();

    var scrim = getScrim(panel);
    if (scrim) scrim.removeAttribute('data-open');

    var finish = function () {
      if (isOpen(panel)) return; // inzwischen wieder geöffnet → nicht verstecken
      panel.hidden = true;
      panel.removeAttribute('data-state');
      if (scrim) scrim.hidden = true;
    };
    var dur = closeDuration(panel);
    if (dur === 0) finish();
    else {
      var done = false;
      var onEnd = function (e) {
        if (e && e.target !== panel) return;
        if (done) return; done = true;
        panel.removeEventListener('transitionend', onEnd);
        finish();
      };
      panel.addEventListener('transitionend', onEnd);
      var timerId = setTimeout(onEnd, dur + 60); // Fallback, falls keine Transition feuert
      // Abbrechbar machen: open() cancelt das Cleanup bei Re-Open während der Animation.
      panel.__hmCancelClose = function () {
        panel.removeEventListener('transitionend', onEnd);
        clearTimeout(timerId);
        done = true;
      };
    }

    if (entry.trigger && document.contains(entry.trigger)) {
      try { entry.trigger.focus({ preventScroll: true }); } catch (e) { entry.trigger.focus(); }
    }
    if (typeof entry.onClose === 'function') entry.onClose();
    panel.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
  }

  function toggle(panel, opts) { isOpen(panel) ? close(panel) : open(panel, opts); }

  function isOpen(panel) {
    return stack.some(function (e) { return e.panel === panel; });
  }
  function current() { return stack.length ? stack[stack.length - 1] : null; }

  // ---- Globale Tastatur-Steuerung (Escape + Fokus-Trap auf oberstem Overlay) ----
  document.addEventListener('keydown', function (e) {
    var top = current();
    if (!top) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close(top.panel);
      return;
    }
    if (e.key === 'Tab') {
      var f = getFocusable(top.panel);
      if (!f.length) { e.preventDefault(); top.panel.focus(); return; }
      var first = f[0], last = f[f.length - 1], active = document.activeElement;
      if (e.shiftKey && (active === first || !top.panel.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (active === last || !top.panel.contains(active))) {
        e.preventDefault(); first.focus();
      }
    }
  }, true);

  // ---- Deklaratives Wiring ----
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      var id = opener.getAttribute('data-overlay-open');
      var panel = id ? document.getElementById(id) : null;
      if (panel) { e.preventDefault(); open(panel, { trigger: opener }); return; }
    }
    var closer = e.target.closest('[data-overlay-close]');
    if (closer) {
      var host = closer.closest('[role="dialog"], .hm-panel, [data-overlay-panel]');
      e.preventDefault();
      close(host && isOpen(host) ? host : (current() && current().panel));
    }
  });

  // ---- Bottom-Sheet: Drag-to-dismiss (nur Mobile) ----
  // Opt-in: Panel trägt [data-overlay-sheet]; gezogen wird NUR an [data-overlay-grip] (Griff-Linie +
  // Kopfbereich). Das Sheet folgt dem Finger nach unten; über der Schwelle → schließen, sonst zurück-
  // schnappen. Die Scroll-Region darunter bleibt unberührt (Grip = nur der obere Bereich), die normale
  // Schließ-Animation (data-state) wird wiederverwendet. Mausklicks/Buttons im Grip lösen KEINEN Drag aus.
  var drag = null; // { panel, startY, dy, h, id }

  function sheetMode() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function endDrag() {
    if (!drag) return;
    var d = drag; drag = null;
    var panel = d.panel;
    panel.style.transition = '';            // CSS-Transition wieder aktivieren
    panel.style.transform = '';             // Inline-Transform lösen → CSS übernimmt wieder
    var threshold = Math.max(80, d.h * 0.25);
    if (d.dy > threshold) {
      close(panel);                         // schließt aus aktueller Position nach unten (data-state)
    }
    // sonst: transform='' animiert von translateY(dy) zurück auf 0 (Snap-back)
  }

  document.addEventListener('pointerdown', function (e) {
    if (drag || !sheetMode()) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!e.target.closest) return;
    var grip = e.target.closest('[data-overlay-grip]');
    if (!grip) return;
    if (e.target.closest('button, a, input, select, textarea, [role="button"]')) return; // Bedienelemente nicht kapern
    var panel = grip.closest('[data-overlay-sheet]');
    if (!panel || !isOpen(panel)) return;
    drag = { panel: panel, startY: e.clientY, dy: 0, h: panel.offsetHeight || 0, id: e.pointerId };
    panel.style.transition = 'none';
  }, true);

  document.addEventListener('pointermove', function (e) {
    if (!drag || e.pointerId !== drag.id) return;
    var dy = e.clientY - drag.startY;
    if (dy < 0) dy = 0;                      // nur nach unten ziehbar
    drag.dy = dy;
    if (dy > 0 && e.cancelable) e.preventDefault();
    drag.panel.style.transform = 'translateY(' + dy + 'px)';
  }, { passive: false });

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  Homedia.overlay = {
    open: open, close: close, toggle: toggle,
    isOpen: isOpen, current: current, getFocusable: getFocusable,
    restoreFocus: restoreFocus, reducedMotion: reducedMotion
  };
})();
